import * as z from "zod";

const ACCEPTED_IMAGE_TYPES = [
  "image/jpg",
  "image/png",
  "image/jpeg",
  undefined,
  null,
];

export const ServiceFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  amount: z.number().optional(),
  duration: z.number().optional(),
  isOpenTime: z.boolean().optional(),
  upgrade: z.number().optional(),
  isActive: z.boolean().optional(),
  isSubscription: z.boolean().optional(),
  days: z.number().optional(),
  tenant: z.string().optional(),
});
export const TenantFormRule = z.object({
  owner: z.string().optional(),
  businessName: z.string().optional(),
  businessType: z.string().optional(),
  description: z.string().optional(),
  avatar: z.string().optional(),
  email: z.string().optional(),
  mobileNumber: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  membershipDate: z.coerce.date().optional().nullish().or(z.string().max(0)),
  rewardsType: z.string().optional(),
  slug: z.string().optional(),
});
export const PersonFormRule = z.object({
  tenant: z.string().optional(),
  role: z.string().optional(),
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  username: z.string().optional(),
  email: z.string().optional(),
  password: z.string().optional(),
  isVerified: z.boolean().optional(),
  avatar: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.coerce.date().optional().nullish().or(z.string().max(0)),
  isBlocked: z.boolean().optional(),
  mobileNumber: z.string().optional(),
  fatherName: z.string().optional(),
  motherName: z.string().optional(),
  emergencyContact_1Number: z.string().optional(),
  emergencyContact_1Name: z.string().optional(),
  emergencyContact_1Relationship: z.string().optional(),
  emergencyContact_2Number: z.string().optional(),
  emergencyContact_2Name: z.string().optional(),
  emergencyContact_2Relationship: z.string().optional(),
  sss: z.string().optional(),
  philhealth: z.string().optional(),
  pagibig: z.string().optional(),
  tin: z.string().optional(),
  medicalCondition: z.string().optional(),
  bloodType: z.string().optional(),
  elementary: z.string().optional(),
  highSchool: z.string().optional(),
  college: z.string().optional(),
  postCollege: z.string().optional(),
  undergraduate: z.string().optional(),
  curriculumVitae: z.string().optional(),
  supportingDoc: z.string().optional(),
  intentLetter: z.string().optional(),
  applicationLetter: z.string().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
  address: z.string().optional(),
});
export const RoleFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});
export const MemberFormRule = z.object({
  tenant: z.string().optional(),
  parentCode: z.string().optional(),
  firstName: z.string().min(1, "First name is required"),
  middleName: z.string().optional(),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().optional(),
  gender: z.string().optional(),
  birthday: z.string().optional(),
  mobileNumber: z.string().optional(),
  avatar: z.string().optional(),
  emergencyContact_1Number: z.string().optional(),
  emergencyContact_1Name: z.string().optional(),
  emergencyContact_1Relationship: z.string().optional(),
  emergencyContact_2Number: z.string().optional(),
  emergencyContact_2Name: z.string().optional(),
  emergencyContact_2Relationship: z.string().optional(),
  streetName: z.string().optional(),
  subdivision: z.string().optional(),
  zone: z.string().optional(),
  sitio: z.string().optional(),
  purok: z.string().optional(),
  barangay: z.string().optional(),
  municipality: z.string().optional(),
  province: z.string().optional(),
  region: z.string().optional(),
  country: z.string().optional(),
  zip: z.string().optional(),
  longitude: z.number().optional(),
  latitude: z.number().optional(),
  isChild: z.boolean().optional(),
  isGuardian: z.boolean().optional(),
  photo: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
  age: z.number(),
});
export const ItemFormRule = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  amount: z.number().optional(),
  tenant: z.string().optional(),
  duration: z.number().optional(),
});
export const TransactionServiceFormRule = z.object({
  batch: z.string().optional(),
  member: z.string().optional(),
  service: z.string().min(1, "Service is required"),
  date: z.string().optional(),
  time: z.string().optional(),
  amount: z.number().optional(),
  serviceData: z.any().optional(),
  guardian: z.string().optional(),
});
export const TransactionAddonFormRule = z.object({
  batch: z.string().optional(),
  item: z.string().optional(),
  quantity: z.number().min(1, "Please input at least a quantity"),
  amount: z.number().min(1, "Please input an amount"),
  itemData: z.any().optional(),
});
export const TransactionFormRule = z.object({
  service: TransactionServiceFormRule.array().optional(),
  addon: TransactionAddonFormRule.array().optional(),
  isPaid: z.boolean().optional(),
});
export const WaiverFormRule = z.object({
  tenant: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  fontSize: z.number().optional(),
  subTitle: z.string().optional(),
});
export const TransactionWaiverFormRule = z.object({
  batch: z.string().optional(),
  waiver: z.string().optional(),
  signature: z.string().optional(),
  waiverMeta: z.any().optional(),
  file: z
    .any()
    .optional()
    .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, and .png formats are supported."
    ),
});
export const SubscriptionFormRule = z.object({
  tenant: z.string().optional(),
  member: z.string().optional(),
  service: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  amount: z.number().optional(),
  isSubscription: z.boolean().optional(),
});
// RULE ADD
export const LoginRule = z.object({
  username: z.string().min(1, "Email/Username is required"),
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormSchema = z.infer<typeof LoginRule>;
export type ServiceFormSchema = z.infer<typeof ServiceFormRule>;
export type TenantFormSchema = z.infer<typeof TenantFormRule>;
export type PersonFormSchema = z.infer<typeof PersonFormRule>;
export type RoleFormSchema = z.infer<typeof RoleFormRule>;
export type MemberFormSchema = z.infer<typeof MemberFormRule>;
export type TransactionServiceFormSchema = z.infer<
  typeof TransactionServiceFormRule
>;
export type TransactionAddonFormSchema = z.infer<
  typeof TransactionAddonFormRule
>;
export type TransactionFormSchema = z.infer<typeof TransactionFormRule>;
export type ItemFormSchema = z.infer<typeof ItemFormRule>;
export type WaiverFormSchema = z.infer<typeof WaiverFormRule>;
export type TransactionWaiverFormSchema = z.infer<
  typeof TransactionWaiverFormRule
>;
export type SubscriptionFormSchema = z.infer<typeof SubscriptionFormRule>;
// SCHEMA ADD
